import React, { useDeferredValue, useMemo } from 'react';
import Card from './Card';
import { useAppSelector } from '../store/hooks';
import { Transaction } from '../types'; // Import Transaction type
import CurrencyDisplay from './AmountDisplay';
import MonthlyCashFlowSkeleton from './skeletons/MonthlyCashFlowSkeleton'; // Import the skeleton
import { useNavigation } from '../hooks/useNavigation';
import { LuMinus, LuPlus } from 'react-icons/lu';
import Badge from './Badge';
import { selectTagMap } from '../store/slices/tagsSlice';
import { isInvestmentDebitTransaction } from '../utils/cashFlowUtils';
import { getCurrentKolkataYearMonth, parseTransactionDateParts } from '../utils/dateTimeUtils';

interface MonthlyCashFlow {
    month: string;
    year: number;
    incoming: number;
    outgoing: number;
    invested: number;
}

const MonthlyCashFlowCard: React.FC = () => {
    const { navigateTo } = useNavigation();
    const {
        transactions, // Use main transactions list
        status,       // Use main status
    } = useAppSelector((state) => state.transactions);
    const tagMap = useAppSelector(selectTagMap);
    const deferredTransactions = useDeferredValue(transactions);
    const deferredTagMap = useDeferredValue(tagMap);

    const isLoading = status === 'loading';

    const monthlyTotalsMap = useMemo(() => {
        const totalsByMonth = new Map<string, MonthlyCashFlow>();

        deferredTransactions.forEach((tx: Transaction) => {
            if (tx.excludeFromAccounting) {
                return;
            }

            const txDate = parseTransactionDateParts(tx.transactionDate);
            const monthKey = `${txDate.year}-${txDate.month}`;
            const monthTotals = totalsByMonth.get(monthKey) ?? {
                month: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, txDate.month - 1, 1))).toUpperCase(),
                year: txDate.year,
                incoming: 0,
                outgoing: 0,
                invested: 0
            };

            if (tx.type === 'CREDIT') {
                monthTotals.incoming += tx.amount;
            } else if (isInvestmentDebitTransaction(tx, deferredTagMap)) {
                monthTotals.invested += tx.amount;
            } else {
                monthTotals.outgoing += tx.amount;
            }

            totalsByMonth.set(monthKey, monthTotals);
        });

        return totalsByMonth;
    }, [deferredTransactions, deferredTagMap]);

    const monthlyData = useMemo((): MonthlyCashFlow[] => {
        if (status === 'loading') {
            return [];
        }

        // Generate array of last 3 months
        const months: MonthlyCashFlow[] = [];
        const currentYearMonth = getCurrentKolkataYearMonth();

        for (let i = 2; i >= 0; i--) {
            const monthIndex = currentYearMonth.year * 12 + (currentYearMonth.month - 1) - i;
            const year = Math.floor(monthIndex / 12);
            const month = (monthIndex % 12) + 1;
            const monthName = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, month - 1, 1))).toUpperCase();
            const monthKey = `${year}-${month}`;
            const monthTotals = monthlyTotalsMap.get(monthKey);

            months.push({
                month: monthName,
                year,
                incoming: monthTotals?.incoming ?? 0,
                outgoing: monthTotals?.outgoing ?? 0,
                invested: monthTotals?.invested ?? 0
            });
        }

        return months;
    }, [monthlyTotalsMap, status]);

    const handleCardClick = () => {
        navigateTo('/cash-flow');
    };

    return (
        <Card
            className="flex flex-col cursor-pointer"
            onClick={handleCardClick}
        >
            {/* Content Area */}
            <div className="p-4 space-y-4 flex-grow overflow-y-auto bg-secondary">
                <div className="flex flex-row text-muted-foreground items-center justify-between">
                    <h1 className="font-bold">Cash Flow</h1>
                    <div className="text-xs font-semibold text-muted-foreground">
                        Last 3 Months
                    </div>
                </div>

                {isLoading && <MonthlyCashFlowSkeleton />}
                {!isLoading && (
                    <div className="space-y-2">
                        {monthlyData.map((monthData, index) => (
                            <div key={`${monthData.year}-${monthData.month}`}>
                                <div className="grid grid-cols-3 items-center font-medium">
                                    <div className="flex flex-grow flex-col justify-between items-start">
                                        <CurrencyDisplay
                                            className="font-bold"
                                            amount={monthData.incoming}
                                            type="CREDIT"
                                            showFraction={false}
                                            showType={false}
                                            smallRupeeSymbol={false}
                                        />
                                    </div>
                                    <div className="flex flex-row items-center justify-center gap-1 text-xs font-mono font-semibold text-muted-foreground text-center">
                                        <LuPlus />
                                        <Badge variant="default" className="font-mono uppercase tracking-wide px-2.5 py-1">
                                            {monthData.month}
                                        </Badge>
                                        <LuMinus />
                                    </div>
                                    <div className="flex flex-grow flex-col justify-between items-end">

                                        <CurrencyDisplay
                                            className="font-bold"
                                            amount={monthData.outgoing}
                                            type="DEBIT"
                                            showFraction={false}
                                            showType={false}
                                            smallRupeeSymbol={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default MonthlyCashFlowCard; 
